import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS, LESSON_STAGE_OPTIONS } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { getClassDropdown } from '@/service/commonService';
import { IStudentFilterValues } from '@/types';

const StudentFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
}: {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IStudentFilterValues) => void;
  initialValues?: Partial<IStudentFilterValues>;
}) => {
  const { hasAccess } = useAccess();
  const { id } = useParams();

  const { formatText } = useFormatText();
  const [status, setStatus] = useState(initialValues.status || '');
  const [levels, setLevels] = useState<string[]>(initialValues.levels || []);
  const [assignedClass, setAssignedClass] = useState<string[]>(
    initialValues.assignedClass || []
  );
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const fetchClass = async () => {
      const response = await getClassDropdown(
        id ? { school_id: id } : undefined
      );
      if (response?.status) {
        const options =
          response?.data?.map((item: { id: string; name: string }) => ({
            value: item?.id,
            label: item?.name,
          })) || [];
        setClassOptions(options);
      }
    };
    fetchClass();
  }, [id]);

  const handleClear = () => {
    setStatus('');
    setLevels([]);
    setAssignedClass([]);
    onApply({ status: '', levels: [], assignedClass: [] });
    setIsOpen(false);
  };

  const handleApply = () => {
    const filters = { status, levels, assignedClass };
    onApply(filters);
    setIsOpen(false);
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled:
          !status && levels?.length === 0 && assignedClass?.length === 0,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Status */}
        <RadioButtonGroup
          name="status"
          label={formatText('user-management.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={formatText}
        />

        {/* Levels */}
        <Select
          name={formatText('user-management.filters.levels')}
          options={LESSON_STAGE_OPTIONS}
          selected={levels}
          onChange={(value) => setLevels(Array.isArray(value) ? value : [])}
          placeholder={formatText('user-management.filters.select-level')}
          isMulti={true}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
        />

        {/* Class */}
        <Select
          name={formatText('user-management.filters.classes')}
          options={classOptions}
          selected={assignedClass}
          onChange={(value) =>
            setAssignedClass(Array.isArray(value) ? value : [])
          }
          placeholder={formatText('user-management.filters.select-class')}
          isMulti={true}
          isClearable={true}
          className="min-h-13"
        />
      </div>
    </CustomModal>
  );
};

export default StudentFilterModal;
